// src/services/menuService.ts
import navigationRoutes, { INavigationRoute } from '@/components/sidebar/NavigationRoutes'
import { toRaw } from 'vue'
import { usePermissionStore } from '@/stores/permission-store'
import { useAuthStore } from '@/stores/auth-store'
import { TokenData } from '@/types/token-data'

/**
 * Transforms and filters navigation routes based on user permissions.
 * @param routes The full set of navigation routes.
 * @param userPermissions The user's permissions.
 * @returns A filtered array of navigation routes.
 */
const buildMenu = (
  routes: INavigationRoute[],
  userPermissions: string[],
  user: TokenData | null,
): INavigationRoute[] => {
  return routes.reduce<INavigationRoute[]>((acc, route) => {
    // If the route has requiredPermissions, check if the user has any of them
    if (route.requiredPermissions) {
      const hasPermission = route.requiredPermissions.some((permission) => userPermissions.includes(permission))
      if (!hasPermission) {
        return acc // Skip this route
      }
    }

    if (route.condition && !route.condition(user)) {
      return acc // Skip this route
    }

    // If the route has children, recursively filter them
    const filteredRoute: INavigationRoute = { ...route }
    if (route.children && route.children.length > 0) {
      const filteredChildren = buildMenu(route.children, userPermissions, user)
      if (filteredChildren.length > 0) {
        filteredRoute.children = filteredChildren
      } else {
        delete filteredRoute.children
      }
    }

    acc.push(filteredRoute)
    return acc
  }, [])
}

/**
 * Fetches and builds the navigation menu based on user permissions.
 * @returns A filtered array of navigation routes.
 */
export const fetchMenuBasedOnPermissions = async (): Promise<INavigationRoute[]> => {
  // Fetch user permissions
  const permissionStore = usePermissionStore()
  if (!permissionStore.permissions || permissionStore.permissions.length === 0) {
    await permissionStore.loadPermissions()
  }

  const userPermissions = permissionStore.permissions
  const authStore = useAuthStore()

  // Build the menu based on permissions
  const filteredRoutes = buildMenu(navigationRoutes.routes, userPermissions, authStore.tokenData)
  return toRaw(filteredRoutes)
}
